import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type {
  PatchedClearanceNote,
  PatchedNeedNote,
  PatchedOpportunityNote,
} from '../../../js/api';
import { useCurrentUser } from '../../../js/app/hooks/auth';
import { handleApiError } from '../../../js/app/utils/errorhandling';
import type {
  ActivityNote,
  ActivityStatusHistory,
  ActivityHistory,
  ActivityEventHistory,
  UnifiedActivity,
} from '../utils/activity-types';
import { formatDateTo24Hr } from '../utils/date';

import type { NoteMutations } from './activity-log';

import { AvatarIcon } from './avatar-icon';
import { Button } from './button';
import { Dialog, DialogTitle, DialogBody, DialogActions } from './dialog';
import { FieldValidationErrorMessage } from './field-validation-error-message';
import { Textarea } from './textarea';
import { TooltippedText } from './tooltipped-text';

type ActivityItemProps = {
  activity: UnifiedActivity;
  noteMutations?: NoteMutations;
};

const noteFormSchema = z.object({
  comment: z.string().min(1, 'Comment is required'),
});

type NoteFormData = z.infer<typeof noteFormSchema>;

const boldStyles = 'tw:text-zinc-900 tw:font-medium';

function getCreatedByDisplayName(
  activityObject: ActivityNote | ActivityStatusHistory | ActivityHistory | ActivityEventHistory,
  currentUserEmail: string | undefined
): string {
  // ActivityEventHistory doesn't have created_by field
  // eslint-disable-next-line sonarjs/no-collapsible-if
  if ('created_by' in activityObject) {
    if (currentUserEmail && activityObject.created_by === currentUserEmail) {
      return 'You';
    }
  }

  if (activityObject.created_by_name && activityObject.created_by_name !== '') {
    return activityObject.created_by_name;
  }

  // ActivityEventHistory doesn't have created_by field
  if ('created_by' in activityObject) {
    return activityObject.created_by || '';
  }
  return '';
}

function NoteActivityItem({
  note,
  noteMutations,
  createdByDisplayName,
  renderHeader,
}: {
  note: ActivityNote;
  noteMutations?: NoteMutations;
  createdByDisplayName: string;
  renderHeader?: () => React.ReactNode;
}) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const editNoteForm = useForm<NoteFormData>({
    resolver: zodResolver(noteFormSchema),
    defaultValues: {
      comment: note.comment || '',
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: async (data: PatchedOpportunityNote | PatchedNeedNote | PatchedClearanceNote) => {
      if (!noteMutations) {
        throw new Error('Note mutations not provided');
      }
      return await noteMutations.updateNote(note.id, data);
    },
    onSuccess: () => {
      toast.success('Note updated successfully');
      setIsEditing(false);
      if (noteMutations?.notesQueryKey) {
        queryClient.invalidateQueries({ queryKey: noteMutations.notesQueryKey });
      }
      if (noteMutations?.extraInvalidateQueryKeys) {
        noteMutations.extraInvalidateQueryKeys.forEach((key) =>
          queryClient.invalidateQueries({ queryKey: key })
        );
      }
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error updating note' });
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async () => {
      if (!noteMutations) {
        throw new Error('Note mutations not provided');
      }
      return await noteMutations.deleteNote(note.id);
    },
    onSuccess: () => {
      toast.success('Note deleted successfully');
      if (noteMutations?.notesQueryKey) {
        queryClient.invalidateQueries({ queryKey: noteMutations.notesQueryKey });
      }
      if (noteMutations?.extraInvalidateQueryKeys) {
        noteMutations.extraInvalidateQueryKeys.forEach((key) =>
          queryClient.invalidateQueries({ queryKey: key })
        );
      }
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error deleting note' });
    },
  });

  const handleEdit = () => {
    setIsEditing(true);
    editNoteForm.reset({
      comment: note.comment || '',
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    editNoteForm.reset({
      comment: note.comment || '',
    });
  };

  const handleSave = editNoteForm.handleSubmit(async (data) => {
    await updateNoteMutation.mutateAsync({
      comment: data.comment,
    });
  });

  const handleDelete = () => {
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = () => {
    deleteNoteMutation.mutate();
    setShowDeleteConfirmation(false);
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  return (
    <>
      <Dialog open={showDeleteConfirmation} onClose={cancelDelete}>
        <DialogTitle>Delete Note</DialogTitle>
        <DialogBody>
          <div>Are you sure you want to delete this note? This action cannot be undone.</div>
        </DialogBody>
        <DialogActions>
          <Button outline onClick={cancelDelete}>
            Cancel
          </Button>
          <Button color="stoplight-danger" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <div className="tw:flex-1 tw:min-w-0">
        <div className="tw:flex tw:items-center tw:justify-between tw:w-full">
          <div className="tw:flex tw:items-center tw:justify-between tw:w-full tw:mb-1 tw:mr-3">
            {renderHeader ? (
              renderHeader()
            ) : (
              <span>
                <span className={boldStyles}>{createdByDisplayName}</span>{' '}
                <span className="tw:text-zinc-500">commented</span>
              </span>
            )}
          </div>
          {note.allow_edit && noteMutations && (
            <div className="tw:flex tw:gap-2 tw:items-center tw:mb-1">
              <button
                type="button"
                onClick={handleEdit}
                className="tw:text-zinc-500 tw:hover:text-zinc-400 tw:dark:text-zinc-400 tw:dark:hover:text-zinc-300 tw:transition-colors"
                aria-label="Edit note"
              >
                <i className="fas fa-edit tw:text-sm" />
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteNoteMutation.isPending}
                className="tw:text-stoplight-danger tw:hover:text-stoplight-danger/50 tw:dark:text-zinc-400 tw:dark:hover:text-stoplight-danger tw:transition-colors tw:disabled:opacity-50"
                aria-label="Delete note"
              >
                <i className="far fa-trash-can tw:text-sm" />
              </button>
            </div>
          )}
        </div>

        {isEditing ? (
          <form onSubmit={handleSave} className="tw:space-y-3">
            <div className="tw:rounded-lg tw:p-1">
              <div className="tw:space-y-3">
                <Textarea
                  {...editNoteForm.register('comment')}
                  placeholder="Enter a comment..."
                  rows={4}
                  disabled={updateNoteMutation.isPending}
                />
                {editNoteForm.formState.errors.comment && (
                  <FieldValidationErrorMessage
                    message={editNoteForm.formState.errors.comment.message}
                  />
                )}
              </div>
            </div>
            <div className="tw:flex tw:justify-end tw:gap-2">
              <Button
                type="button"
                outline
                onClick={handleCancel}
                disabled={updateNoteMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" color="brandgreen0" disabled={updateNoteMutation.isPending}>
                {updateNoteMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        ) : (
          <>
            <div className="tw:flex tw:flex-col tw:gap-2  tw:rounded-lg tw:p-1 tw:mb-1">
              <div className="tw:text-sm tw:text-zinc-900 tw:whitespace-pre-line ">
                {note.comment}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function StatusActivityItem({
  status,
  createdByDisplayName,
  noteMutations,
}: {
  status: ActivityStatusHistory;
  createdByDisplayName: string;
  noteMutations?: NoteMutations;
}) {
  const fromStatusTitle = status.from_status?.title;
  const toStatusTitle = status.to_status?.title;
  const toReasonTitle = status.to_reason?.title;

  const header = () => (
    <span className="tw:text-zinc-500 tw:text-sm">
      <span className={boldStyles}>{createdByDisplayName}</span>{' '}
      {fromStatusTitle ? (
        <>
          changed status to{' '}
          <span className={boldStyles}>
            {toStatusTitle}
            {toReasonTitle ? `: ${toReasonTitle}` : ''}
          </span>
        </>
      ) : (
        <>
          created with status <span className={boldStyles}>{toStatusTitle}</span>
        </>
      )}
    </span>
  );

  return (
    <div className="tw:flex-1 tw:min-w-0">
      {status.note ? (
        <NoteActivityItem
          note={status.note}
          noteMutations={noteMutations}
          createdByDisplayName={createdByDisplayName}
          renderHeader={header}
        />
      ) : (
        <>
          <div className="tw:flex tw:items-center tw:justify-between tw:w-full tw:mb-1">
            {header()}
          </div>
          {status.note_id && (
            <div
              id={status.note_id}
              className="tw:flex tw:flex-col tw:gap-2 tw:bg-zinc-100 tw:rounded-lg tw:p-3 tw:mb-1"
            >
              <div className="tw:text-sm tw:text-zinc-900 tw:whitespace-pre-line ">
                {status.comment}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function EventActivityItem({
  eventHistory,
  createdByDisplayName,
}: {
  eventHistory: ActivityEventHistory;
  createdByDisplayName: string;
}) {
  return (
    <div className="tw:flex-1 tw:min-w-0">
      <div className="tw:flex tw:items-center tw:justify-between tw:w-full tw:mb-1">
        <span className="tw:text-zinc-500">
          <span className={boldStyles}>{createdByDisplayName}</span>{' '}
          <span className={boldStyles}>{eventHistory.event.title}</span>
        </span>
      </div>
    </div>
  );
}

function UpdateActivityItem({
  change,
  createdByDisplayName,
}: {
  change: ActivityHistory;
  createdByDisplayName: string;
}) {
  const hasBatchChanges = change.batch_changes && change.batch_changes.length > 0;
  const isMultipleFields = hasBatchChanges && change.batch_changes.length > 1;
  const [showBatchChangesDialog, setShowBatchChangesDialog] = useState(false);

  return (
    <>
      <Dialog
        open={showBatchChangesDialog}
        onClose={() => setShowBatchChangesDialog(false)}
        size="3xl"
      >
        <DialogBody>
          <div className="tw:grid tw:grid-cols-3 tw:gap-3 tw:pb-2 tw:border-b tw:border-zinc-200 tw:mb-4 tw:text-xs tw:font-semibold tw:text-zinc-500 tw:uppercase tw:tracking-wider">
            <div>Name</div>
            <div>Old Value</div>
            <div>New Value</div>
          </div>
          <div className="tw:space-y-2">
            {change.batch_changes?.map((batchChange, index) => (
              <div
                key={index}
                className="tw:grid tw:grid-cols-3 tw:gap-3 tw:border-b tw:border-zinc-200 tw:dark:border-zinc-700 tw:pb-2 tw:last:border-b-0"
              >
                <div className="tw:text-sm tw:font-medium tw:text-zinc-900 tw:dark:text-zinc-100">
                  {batchChange.label || batchChange.field_name || `Field ${index + 1}`}
                </div>
                <div className="tw:text-sm tw:text-stoplight-danger tw:dark:text-zinc-400 tw:break-words">
                  {batchChange.old_value_display || batchChange.old_value_display === 0 ? (
                    batchChange.old_value_display
                  ) : (
                    <span className="tw:italic tw:text-zinc-400">Empty</span>
                  )}
                </div>
                <div className="tw:text-sm tw:text-stoplight-success tw:dark:text-zinc-100 tw:font-medium tw:break-words">
                  {batchChange.new_value_display || batchChange.new_value_display === 0 ? (
                    batchChange.new_value_display
                  ) : (
                    <span className="tw:italic tw:text-zinc-400">Empty</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogBody>
        <DialogActions>
          <Button color="brand-green-outline" onClick={() => setShowBatchChangesDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <div className="tw:flex-1 tw:min-w-0">
        <div className="tw:flex tw:items-center tw:justify-between tw:w-full tw:mb-1">
          <div className="tw:flex tw:items-center tw:gap-2">
            <span className="tw:text-sm tw:text-zinc-500">
              {isMultipleFields ? (
                <>
                  <span className={boldStyles}>{createdByDisplayName}</span> updated{' '}
                  <span className={boldStyles}>multiple fields</span>
                </>
              ) : (
                <>
                  <span className={boldStyles}>{createdByDisplayName}</span>{' '}
                  {change.old_value_display ? (
                    !change.new_value_display ? (
                      <>
                        cleared <span className={boldStyles}>{change.label}</span>
                      </>
                    ) : (
                      <>
                        updated <span className={boldStyles}>{change.label}</span> from{' '}
                        <span className={boldStyles}>{change.old_value_display}</span> to{' '}
                        <span className={boldStyles}>{change.new_value_display}</span>
                      </>
                    )
                  ) : (
                    <>
                      set <span className={boldStyles}>{change.label}</span> to{' '}
                      <span className={boldStyles}>{change.new_value_display}</span>
                    </>
                  )}
                </>
              )}
            </span>
            {isMultipleFields && (
              <Button
                type="button"
                small
                outline
                aria-label="Show diff"
                onClick={() => setShowBatchChangesDialog(true)}
              >
                See All
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export function ActivityItem({ activity, noteMutations }: ActivityItemProps) {
  const { user } = useCurrentUser();
  const currentUserEmail = user?.email;
  const createdByDisplayName = getCreatedByDisplayName(activity.activity_object, currentUserEmail);

  let activityContent: React.ReactNode;

  switch (activity.activity_type) {
    case 'note':
      activityContent = (
        <NoteActivityItem
          note={activity.activity_object}
          noteMutations={noteMutations}
          createdByDisplayName={createdByDisplayName}
        />
      );
      break;
    case 'status':
      activityContent = (
        <StatusActivityItem
          status={activity.activity_object}
          createdByDisplayName={createdByDisplayName}
          noteMutations={noteMutations}
        />
      );
      break;
    case 'update':
      activityContent = (
        <UpdateActivityItem
          change={activity.activity_object}
          createdByDisplayName={createdByDisplayName}
        />
      );
      break;
    case 'event':
      activityContent = (
        <EventActivityItem
          eventHistory={activity.activity_object}
          createdByDisplayName={createdByDisplayName}
        />
      );
      break;
    default:
      activityContent = null;
  }

  return (
    <div className="tw:flex tw:gap-2 tw:border-b tw:border-zinc-200 tw:py-2">
      <TooltippedText tooltipContent={activity.activity_type + ' activity'}>
        <AvatarIcon
          iconClassName={activity.activity_icon}
          className="tw:size-8 tw:shrink-0 tw:text-brand-green-0 tw:outline-none"
        />
      </TooltippedText>
      <div className="tw:flex tw:flex-col tw:w-full">
        <span className="tw:text-xs tw:font-medium tw:text-zinc-400">
          {formatDateTo24Hr(activity.activity_datetime, 'EE, MM/dd/yyyy HH:mm')}
          {((activity.activity_type === 'note' && activity.activity_object.was_edited) ||
            (activity.activity_type === 'status' &&
              'note' in activity.activity_object &&
              activity.activity_object.note?.was_edited)) &&
            ' (edited)'}
        </span>
        {activityContent}
      </div>
    </div>
  );
}
